import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ExternalLink, Heart, Eye, Calendar } from 'lucide-react';

const SocialWidget = () => {
  const socialPlatforms = [
    {
      name: 'Instagram',
      handle: '@rolluplumpiaco',
      url: 'https://instagram.com/rolluplumpiaco',
      color: 'from-pink-500 to-purple-600',
      icon: '📸',
      description: 'Follow for daily lumpia updates and behind-the-scenes content'
    },
    {
      name: 'Facebook', 
      handle: 'Roll Up Lumpia Co.',
      url: 'https://facebook.com/rolluplumpiaco',
      color: 'from-blue-600 to-blue-700',
      icon: '👍',
      description: 'Like our page for event announcements and customer reviews'
    },
    {
      name: 'TikTok',
      handle: 'Rolluplumpiaco',
      url: 'https://tiktok.com/@rolluplumpiaco', 
      color: 'from-black to-red-600',
      icon: '🎵',
      description: 'Watch our lumpia-making process and fun cooking videos'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            See Where I'll Be Next
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            I'm out in the community at local markets and events all the time! Follow me on social
            media to catch my latest pop-up locations, behind-the-scenes content, and mouth-watering
            lumpia photos.
          </p>
        </div>

        {/* Social Platforms Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {socialPlatforms.map((platform, index) => (
            <Card key={index} className="hover:shadow-xl transition-all duration-300 border-2 border-blue-200 hover:border-blue-400 bg-white overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${platform.color} flex items-center justify-center text-white font-bold text-lg`}>
                      {platform.icon}
                    </div>
                    <div>
                      <CardTitle className="text-lg font-bold text-blue-900">
                        {platform.name}
                      </CardTitle>
                      <p className="text-sm text-blue-600 font-medium">
                        {platform.handle}
                      </p>
                    </div>
                  </div>
                  <a 
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-100 hover:bg-blue-200 p-2 rounded-full transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 text-blue-700" />
                  </a>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-sm text-blue-800 font-medium mb-3">
                    {platform.description}
                  </p>
                  <div className="text-center">
                    <p className="text-xs text-blue-600 mb-3">
                      Visit our {platform.name} for the latest updates!
                    </p>
                  </div>
                </div>
                
                {/* Follow Button */}
                <a 
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 shadow-md bg-gradient-to-r ${platform.color} hover:shadow-lg flex items-center justify-center space-x-2`}
                >
                  <span>Follow on {platform.name}</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-r from-yellow-400 to-amber-400 border-2 border-yellow-500 shadow-xl">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Tag us in your lumpia photos!
              </h3>
              <p className="text-gray-800 font-medium mb-6">
                Use #RollUpLumpia and get featured on our social media
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Badge className="bg-white text-gray-900 px-4 py-2 font-semibold">
                  #RollUpLumpia
                </Badge>
                <Badge className="bg-white text-gray-900 px-4 py-2 font-semibold">
                  #LumpiaLife
                </Badge>
                <Badge className="bg-white text-gray-900 px-4 py-2 font-semibold">
                  #FilipinoCuisine
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default SocialWidget;